// Pointer geometry for dragging the Watchlist fill handle.
//
// The grid has one scroll element whose content starts with the sticky
// header; display row `i` occupies content y from `headerHeight + i *
// rowHeight` for `rowHeight` px. `clientY`, `scrollerTop` and `scrollerBottom`
// are viewport pixels (pointer events, getBoundingClientRect); `scrollTop` is
// the scroll element's offset. Ranges are inclusive display-index ranges, the
// same shape fillPattern's planFill takes.

export const AUTO_SCROLL_EDGE = 40;
export const AUTO_SCROLL_MAX_SPEED = 20;

/**
 * The display row under the pointer, clamped to the rows that exist. A pointer
 * over the sticky header or above the grid counts as the first row visible
 * below the header; given `scrollerBottom`, a pointer below the grid counts as
 * the last visible row, so a fill never reaches rows the user cannot see.
 * Null when there are no rows.
 */
export function rowIndexAtPoint({
    clientY,
    scrollerTop,
    scrollerBottom,
    scrollTop = 0,
    headerHeight = 0,
    rowHeight,
    rowCount,
}) {
    if (!(rowCount > 0) || !(rowHeight > 0)) return null;
    let viewportY = clientY - scrollerTop;
    if (Number.isFinite(scrollerBottom)) {
        viewportY = Math.min(viewportY, scrollerBottom - scrollerTop - 1);
    }
    viewportY = Math.max(viewportY, headerHeight);
    const index = Math.floor((viewportY - headerHeight + scrollTop) / rowHeight);
    return Math.min(rowCount - 1, Math.max(0, index));
}

/**
 * The rows a fill writes while the pointer is over display row `hoverIndex`:
 * from just past the source block to the pointer, below or above it. Null
 * while the pointer is inside the source block, where there is nothing to fill.
 */
export function fillTargetRange(sourceRange, hoverIndex) {
    if (!sourceRange || !Number.isInteger(hoverIndex)) return null;
    const sourceStart = Math.min(sourceRange.startIndex, sourceRange.endIndex);
    const sourceEnd = Math.max(sourceRange.startIndex, sourceRange.endIndex);
    if (hoverIndex > sourceEnd) {
        return { startIndex: sourceEnd + 1, endIndex: hoverIndex, direction: 'down' };
    }
    if (hoverIndex < sourceStart) {
        return { startIndex: hoverIndex, endIndex: sourceStart - 1, direction: 'up' };
    }
    return null;
}

/**
 * Auto-scroll speed in px per 60 Hz frame while a fill drag is near the top or
 * bottom of the grid: negative scrolls up, positive down, 0 in between.
 *
 * The zones are the first and last `edge` px of the visible body, below the
 * sticky header. Speed grows linearly with how deep the pointer is in a zone
 * and stays at `maxSpeed` past it (over the header or outside the grid). Given
 * `originY`, the pointer's position when the drag started, a zone only scrolls
 * towards the side the pointer has moved to: a drag that starts on a handle
 * near the bottom and heads up must not scroll down.
 */
export function autoScrollVelocity({
    clientY,
    scrollerTop,
    scrollerBottom,
    headerHeight = 0,
    edge = AUTO_SCROLL_EDGE,
    maxSpeed = AUTO_SCROLL_MAX_SPEED,
    originY,
}) {
    const bodyTop = scrollerTop + headerHeight;
    // A short grid gets narrower zones so the two never overlap.
    const zone = Math.min(edge, (scrollerBottom - bodyTop) / 3);
    if (!(zone > 0) || !(maxSpeed > 0)) return 0;
    const hasOrigin = Number.isFinite(originY);

    const upDepth = bodyTop + zone - clientY;
    if (upDepth > 0 && (!hasOrigin || clientY < originY)) {
        return -maxSpeed * Math.min(1, upDepth / zone);
    }
    const downDepth = clientY - (scrollerBottom - zone);
    if (downDepth > 0 && (!hasOrigin || clientY > originY)) {
        return maxSpeed * Math.min(1, downDepth / zone);
    }
    return 0;
}

/**
 * The target rows as a box in content coordinates, the space `selectionRect`
 * uses, spanning the selection's columns.
 */
export function fillTargetRect({ targetRange, selectionRect, headerHeight = 0, rowHeight }) {
    if (!targetRange || !selectionRect) return null;
    return {
        left: selectionRect.left,
        top: headerHeight + (targetRange.startIndex * rowHeight),
        width: selectionRect.width,
        height: (targetRange.endIndex - targetRange.startIndex + 1) * rowHeight,
    };
}
